/**
 * The arithmetic that makes one map readable on a laptop and on a phone.
 *
 * This is the module the viewport problem actually lives in, so it is pure functions with no
 * React in sight — the parts worth testing at three sizes and three aspect ratios.
 *
 * The problem, stated once (`docs/web-api/phase-9-kickoff.md` §3.6): a generated map is 3:2
 * landscape. On a 1280×800 laptop beside a rail it renders about 900×600. On a 390×844 phone in
 * portrait, fit to width, it renders 390×260 — **a fifth of the area**, in a strip taking under a
 * third of the screen. The zone numbers and the legend are baked into the image pixels, so at that
 * scale they are not small, they are unreadable.
 */

/** The size of the box a map is being drawn into. */
export interface Viewport {
  width: number
  height: number
}

/** The map image's own dimensions. Not assumed to be 3:2 — an uploaded map can be any shape. */
export interface ImageSize {
  width: number
  height: number
}

/** Where the map actually sits inside the viewport, once fitted. */
export interface Fit {
  width: number
  height: number
  /** Left offset inside the viewport — the letterbox margin. */
  left: number
  /** Top offset inside the viewport. */
  top: number
}

/**
 * Fits a map into a box, preserving its aspect ratio.
 *
 * `contain`, not `cover`: a battle map cropped to fill a phone is a picture of the middle of a
 * room, and the exits are the part that matters. The letterbox offsets come back with it so a
 * token layer can sit exactly on the image rather than on the box around it.
 */
export function fitContain(viewport: Viewport, image: ImageSize): Fit {
  if (image.width <= 0 || image.height <= 0 || viewport.width <= 0 || viewport.height <= 0) {
    return { width: 0, height: 0, left: 0, top: 0 }
  }

  const scale = Math.min(viewport.width / image.width, viewport.height / image.height)
  const width = image.width * scale
  const height = image.height * scale

  return {
    width,
    height,
    left: (viewport.width - width) / 2,
    top: (viewport.height - height) / 2,
  }
}

/** A normalized `0..1` point turned into pixels inside a fitted map. */
export function toPixels(fit: Fit, x: number, y: number): { left: number; top: number } {
  return { left: fit.left + x * fit.width, top: fit.top + y * fit.height }
}

/**
 * Pixels inside a fitted map turned back into a normalized `0..1` point.
 *
 * Clamped, because a drag that ends past the edge of the map should put the token *at* the edge —
 * a token outside `0..1` is one the only tool that could move it back cannot reach.
 */
export function toNormalized(fit: Fit, left: number, top: number): { x: number; y: number } {
  if (fit.width <= 0 || fit.height <= 0) return { x: 0, y: 0 }

  return {
    x: clamp01((left - fit.left) / fit.width),
    y: clamp01((top - fit.top) / fit.height),
  }
}

export function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value))
}

/**
 * The smallest a token may be drawn, in CSS pixels.
 *
 * 44 is the accessible touch-target minimum, and the GM has to be able to grab one. It is not a
 * nicety on this screen: a token sized purely as a fraction of the map lands at about 30 px for
 * the GM and **13 px on a portrait phone**, which carries no portrait and no label.
 */
export const MIN_TOKEN_PX = 44

/** A token at map scale, before the floor. 5% of the shorter edge — roughly a person on a 5-zone map. */
const TOKEN_FRACTION = 0.05

/**
 * How many pixels across to draw a token, **in the map stage's own untransformed space**.
 *
 * The zoom is deliberately not an input. Tokens live inside the transformed container, so the
 * container's `scale()` grows them along with the map — a token marks a position *on the map*,
 * and one pinned to a constant on-screen size stops covering the ground it is meant to occupy the
 * moment anybody zooms.
 *
 * **Scales with the map down to a floor, then stops** — a deliberate trade rather than a fudge.
 * Below the floor, tokens stop shrinking and start overlapping; the alternative is a token too
 * small to see, hit or identify. Because this is applied before the transform, the floor binds at
 * 1x — the least zoomed the map ever is — which is exactly where it is needed.
 */
export function tokenPixels(fit: Fit, size: number): number {
  const shorterEdge = Math.min(fit.width, fit.height)

  return Math.max(MIN_TOKEN_PX, shorterEdge * TOKEN_FRACTION * size)
}

/**
 * Whether this viewport is too cramped to read a map in.
 *
 * Drives the rotate prompt. The threshold is about the *shape* rather than the size: a map is 3:2
 * or wider, so a portrait box wastes most of its height on nothing and leaves the map a strip.
 * A landscape phone at 844×390 fits a map at 585×390 — 2.25× the area, for free.
 */
export function isTooCramped(viewport: Viewport): boolean {
  return viewport.width < 640 && viewport.height > viewport.width
}

/** Zoom bounds. Below 1 the map is smaller than its own box, which helps nobody. */
export const MIN_ZOOM = 1
export const MAX_ZOOM = 6

export function clampZoom(zoom: number): number {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom))
}

/**
 * Keeps a pan inside the map.
 *
 * Zooming grows the stage past its frame; panning slides it. Without a bound the map can be
 * dragged entirely off screen and the only way back is the Fit button — which is a trap rather
 * than a gesture. The bound is exactly the overflow: at zoom 1 there is none, so the pan is
 * pinned at zero and the map cannot be nudged at all.
 */
export function clampPan(
  pan: { x: number; y: number },
  viewport: Viewport,
  zoom: number,
): { x: number; y: number } {
  const overflowX = Math.max(0, viewport.width * zoom - viewport.width)
  const overflowY = Math.max(0, viewport.height * zoom - viewport.height)

  // `|| 0` normalises negative zero, which `Math.min`/`Math.max` produce here and which compares
  // unequal to 0 under strict equality — a wart that would otherwise surface in a caller's test
  // rather than here.
  return {
    x: Math.min(0, Math.max(-overflowX, pan.x)) || 0,
    y: Math.min(0, Math.max(-overflowY, pan.y)) || 0,
  }
}
